import SearchInput from '../../search-input/search-input';
import { ShoppingCartOutlined, CloseOutlined } from '@ant-design/icons';
import { Badge, Drawer, Menu, MenuProps, Button, Select } from 'antd';
import { Header } from 'antd/es/layout/layout';
import { Link, useNavigate } from 'react-router-dom';
import { useSessionContext } from '../../../context/session-context';
import { useCallback, useEffect, useState } from 'react';
import { SessionStatus } from 'src/constant/sessionStatus';
import { RouteNames } from 'src/routes/routeName';
import { RoleType } from 'src/constant/roleType';
import { useTranslation } from 'react-i18next';
import { LanguageConstants } from 'src/constant/language';
import classStore from 'src/store/classStore';
import TranslationKey from 'src/i18n/translation';
import CartTooltip from './cart-tooltip';

interface NavbarInterface {
  isShowSearch: boolean;
}

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: 'group'
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    type,
  } as MenuItem;
}

export default function Navbar({ isShowSearch = true }: NavbarInterface) {
  const { i18n } = useTranslation();

  const imageLogo = require('../../../assets/images/logo/white@3x.png');
  const imageLogoMobile = require('../../../assets/images/logo/logo-mobile.png');
  const { countOrder, getUserProfileAsync, isAuthenticated, userProfile, signOut, getCountOrder } = useSessionContext();

  const [menus, setMenus] = useState<MenuProps['items']>([]);
  const navigate = useNavigate();

  const [lang, setLang] = useState('');

  useEffect(() => {
    getCountOrder();
    getUserProfileAsync();
    setNavMenus(countOrder);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countOrder, lang, setLang]);

  useEffect(() => {
    setNavMenus(countOrder);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    getLanguage();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const getLanguage = useCallback(
    () => {
      var language = classStore.get(LanguageConstants.key) as string;

      if (language === null || language === undefined) {
        language = LanguageConstants.en;
      }

      setLang(language);
      i18n.changeLanguage(language);
      classStore.set(LanguageConstants.key, language);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

  const setNavMenus = useCallback((orderCount: number) => {
    const cartMenu =
      getItem(
        <CartTooltip count={orderCount} />,
        RouteNames.cart,
        null
      );

    const pricingMenu =
      getItem(
        <a href={RouteNames.pricing}
          style={{ textDecoration: 'none' }}>
          {TranslationKey('hPricing')}
        </a>,
        RouteNames.pricing,
        null
      );

    const mainMenus: MenuProps['items'] = [
      cartMenu,
      pricingMenu,
    ];

    const isSignedIn =
      isAuthenticated === SessionStatus.AUTHENTICATED &&
      userProfile &&
      userProfile.userId &&
      userProfile.roles.length > 0

    if (!isSignedIn) {
      setMenus([
        ...mainMenus,
        ...registerArtistMenu!,
        ...signinMenus!]);
      return;
    }

    const isArtist =
      userProfile.roles
        .filter((r) => r === RoleType.ARTIST)
        .length > 0;

    if (isArtist) {
      setMenus([
        ...mainMenus,
        ...loginArtistMenus!]);
      return;
    }

    setMenus([
      ...mainMenus,
      ...registerArtistMenu!,
      ...profileMenus!]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userProfile]);

  const signinMenus: MenuProps['items'] = [
    getItem(
      <>
        <span
          className='btn-signin'
          style={{ textDecoration: 'none' }}>
          {TranslationKey('button.signIn')}
        </span>
      </>,
      RouteNames.login,
      null
    ),
  ];

  const profileMenus: MenuProps['items'] = [
    getItem(userProfile.email, 'user', null, [
      getItem(
        <Link to={RouteNames.myAsset} style={{ textDecoration: 'none' }}>
          {TranslationKey('button.profile')}
        </Link>,
        RouteNames.myAsset,
        null
      ),
      getItem(TranslationKey('button.signOut'), 'logout', null),
    ]),
  ];

  const registerArtistMenu: MenuProps['items'] = [
    getItem(
      <>
        <span
          className='btn-signin'
          style={{ textDecoration: 'none' }}>
          {TranslationKey('button.registerArtist')}
        </span>
      </>,
      RouteNames.welcomeArtist,
      null
    ),
  ];

  const loginArtistMenus: MenuProps['items'] = [
    getItem(userProfile.email, 'user', null, [
      getItem(
        <Link to={RouteNames.dashboard} style={{ textDecoration: 'none' }}>
          {TranslationKey('button.profile')}
        </Link>,
        RouteNames.dashboard,
        null
      ),
      getItem(TranslationKey('button.signOut'), 'logout', null),
    ]),
  ];

  const onClick: MenuProps['onClick'] = async (e) => {
    if (e.key === RouteNames.cart) {
      return;
    }

    if (e.key !== 'logout') {
      navigate(e.key);

      return;
    }

    await window.localStorage.clear();
    signOut();
  };
  const [visible, setVisible] = useState(false);
  const [width, setWidth] = useState<number>(window.innerWidth);

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }
  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  const isMobile = width <= 768;

  return (
    <>
      <Header className='navbar-fix'>
        <div className='logo'>
          <Link to='/'>
            {isMobile ? (
              <img src={String(imageLogoMobile)} alt='alive united plus' width={50} />
            ) : (
              <img src={String(imageLogo)} alt='alive united plus' />
            )}
          </Link>
        </div>
        <div className='menuCon'>
          {isShowSearch && (
            <div className='navbar-search'>
              <SearchInput />
            </div>
          )}
        </div>
        <div className='right-menu'>
          <Menu
            theme='dark'
            mode='horizontal'
            style={{ float: 'right' }}
            disabledOverflow={true}
            onClick={onClick}
            selectable={false}
            items={menus}
          ></Menu>
        </div>
        <Button size='large' className='bars-menu' type='primary' onClick={() => setVisible(true)}>
          <span className='barsBtn'></span>

        </Button>
        <Drawer
          title={
            <div onClick={() => setVisible(false)} className='flex-end'>
              <CloseOutlined className='text-light fs-3 pe-3' />
            </div>
          }
          placement='right'
          closable={false}
          onClose={() => setVisible(false)}
          className='nav'
          visible={visible}
        >
          <Menu
            theme='dark'
            mode='horizontal'
            disabledOverflow={true}
            onClick={onClick}
            selectable={false}
            items={menus}
          ></Menu>
        </Drawer>
        <Select
          className="input-radius-dark non-radius"
          value={lang}
          style={{ width: 120 }}
          onChange={(value) => {
            setLang(value);
            i18n.changeLanguage(value);
            classStore.set(LanguageConstants.key, value);
            window.location.reload();
          }}
          options={[
            { value: LanguageConstants.en, label: LanguageConstants.labelEn },
            { value: LanguageConstants.th, label: LanguageConstants.labelTh },
          ]}
        />
      </Header>
    </>
  );
}
